import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getKnockoutDrift } from '../api';

const COLORS = ['#3fd66a','#f87171','#60a5fa','#fbbf24','#a78bfa','#22d3ee','#f472b6','#fb923c','#84cc16','#06b6d4','#e879f9','#fb7185'];
const W = 600, H = 220, PADL = 30, PADR = 8, PADT = 8, PADB = 22;

export default function KnockoutDriftChart() {
  const { data } = useQuery({ queryKey: ['ko-drift'], queryFn: getKnockoutDrift });
  const [hovered, setHovered] = useState<string | null>(null);

  const dates = data?.dates ?? [];
  const top = useMemo(() => (data?.series ?? []).slice(0, 12), [data]);
  const maxCup = useMemo(() => {
    let m = 0;
    for (const s of top) for (const p of s.series) if (p.cup_chance > m) m = p.cup_chance;
    return Math.max(m, 0.01);
  }, [top]);

  if (dates.length === 0) {
    return (
      <section className="card p-5 text-center">
        <div className="font-display text-base font-semibold mb-1">Cup-chance drift</div>
        <div className="text-xs text-dim">Daily snapshots populate as the tournament progresses.</div>
      </section>
    );
  }

  const xFor = (i: number) => PADL + (W - PADL - PADR) * (dates.length === 1 ? 0.5 : i / (dates.length - 1));
  const yFor = (v: number) => PADT + (H - PADT - PADB) * (1 - v / maxCup);

  return (
    <section className="card p-3 sm:p-4">
      <div className="flex items-baseline justify-between mb-2">
        <div className="font-display text-base font-semibold">Cup-chance drift (top 12)</div>
        <div className="text-[10px] uppercase tracking-wider text-dim">{dates[0]} → {dates[dates.length - 1]}</div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-[200px] sm:h-[240px] block">
        {[0, 0.5, 1].map((p) => (
          <g key={p}>
            <line x1={PADL} x2={W - PADR} y1={yFor(maxCup * p)} y2={yFor(maxCup * p)} stroke="#1c2230" strokeDasharray={p === 0 ? '' : '3 5'} />
            <text x={PADL - 4} y={yFor(maxCup * p) + 3} textAnchor="end" fontSize="9" fill="#9aa3b2" fontFamily="ui-monospace, monospace">{(maxCup * p * 100).toFixed(0)}%</text>
          </g>
        ))}
        {top.map((s, i) => {
          const color = COLORS[i % COLORS.length];
          const path = s.series.map((p, j) => {
            const dayIdx = dates.indexOf(p.day);
            return `${j === 0 ? 'M' : 'L'} ${xFor(dayIdx).toFixed(1)} ${yFor(p.cup_chance).toFixed(1)}`;
          }).join(' ');
          const dim = hovered && hovered !== s.team_code;
          return <path key={s.team_code} d={path} stroke={color} strokeWidth={1.8} fill="none" opacity={dim ? 0.15 : 1} />;
        })}
      </svg>
      <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1.5 text-[11px]">
        {top.map((s, i) => {
          const c = COLORS[i % COLORS.length];
          const last = s.series[s.series.length - 1]?.cup_chance ?? 0;
          const isH = hovered === s.team_code;
          return (
            <button
              key={s.team_code}
              onMouseEnter={() => setHovered(s.team_code)} onMouseLeave={() => setHovered(null)}
              onClick={() => setHovered(isH ? null : s.team_code)}
              className={`inline-flex items-center gap-1.5 rounded-md px-1.5 py-0.5 ${isH ? 'bg-ink-800' : ''}`}
            >
              <span className="w-2.5 h-2.5 rounded-sm" style={{ background: c }} />
              <span>{s.flag} {s.team_code}</span>
              <span className="stat text-dim">{(last * 100).toFixed(1)}%</span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
